import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import { exploreClasses } from "../../mocks/explore";
import { classContentMap, analyzeChallengeMap } from "../../mocks/classContent";
import StepIndicator from "./components/StepIndicator";
import LearnStep from "./components/LearnStep";
import PracticeStep from "./components/PracticeStep";
import AnalyzeChallengeStep from "./components/AnalyzeChallengeStep";
import SubmitStep from "./components/SubmitStep";
import FeedbackStep from "./components/FeedbackStep";
import FocusTimer from "./components/FocusTimer";

export default function LearnPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cls = exploreClasses.find((c) => c.id === Number(id)) || exploreClasses[0];

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [builtPrompt, setBuiltPrompt] = useState("");

  const content = classContentMap[cls.id] ?? classContentMap[1];
  const hasAnalyze = !!analyzeChallengeMap[cls.id];

  // For analyze classes: 5 steps (Learn, Analyze, Create, Submit, Feedback)
  // For create classes:  4 steps (Learn, Create, Submit, Feedback)
  const submitStep   = hasAnalyze ? 4 : 3;
  const feedbackStep = hasAnalyze ? 5 : 4;

  const completeStep = (step: number) => {
    setCompletedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
    setCurrentStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Analyze done → move to step 3 (PracticeStep in combo flow)
  const handleAnalyzeComplete = (result: string) => {
    setBuiltPrompt(result);
    completeStep(2);
  };

  // Practice done
  const handlePracticeComplete = (prompt: string) => {
    setBuiltPrompt(prompt);
    // For analyze flow: complete step 3; for create flow: complete step 2
    completeStep(hasAnalyze ? 3 : 2);
  };

  const isBackVisible = currentStep > 1 && currentStep < feedbackStep;

  return (
    <div className="min-h-screen bg-[#F2F4F6]">
      <Navbar />

      <div className="pt-20 pb-16 px-6">
        <div className="max-w-3xl mx-auto">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-5 mt-4">
            <button
              onClick={() => navigate("/explore")}
              className="text-[#8B95A1] text-sm hover:text-[#191F28] transition-colors cursor-pointer whitespace-nowrap"
            >
              클래스 탐색
            </button>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-[#8B95A1]" />
            </span>
            <span className="text-[#191F28] text-sm font-medium truncate">{cls.title}</span>
          </div>

          {/* Class Title Card */}
          <div className="bg-white rounded-2xl p-5 mb-5 flex items-center gap-4">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: cls.iconBg }}
            >
              <i className={`${cls.icon} text-xl`} style={{ color: cls.iconColor }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <p className="text-xs text-[#8B95A1] font-medium">{cls.tag}</p>
                {hasAnalyze && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-red-100 to-orange-100 text-red-500">
                    🔍 ACE 콤보 포함
                  </span>
                )}
              </div>
              <h1 className="text-lg font-extrabold text-[#191F28] leading-tight truncate">{cls.title}</h1>
            </div>
            <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
              <span className="text-xs text-[#8B95A1] flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line" /></span>
                {cls.duration}
              </span>
              <span className="text-xs text-[#8B95A1] flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-group-line" /></span>
                {cls.participants.toLocaleString()}명
              </span>
            </div>
          </div>

          {/* Step Indicator */}
          <StepIndicator
            currentStep={currentStep}
            completedSteps={completedSteps}
            analyzeMode={hasAnalyze}
          />

          {/* ── Step 1: Learn ── */}
          {currentStep === 1 && (
            <LearnStep content={content} onComplete={() => completeStep(1)} />
          )}

          {/* ── Step 2: Analyze (analyze classes only) ── */}
          {currentStep === 2 && hasAnalyze && (
            <AnalyzeChallengeStep
              classId={cls.id}
              onComplete={handleAnalyzeComplete}
            />
          )}

          {/* ── Step 2 (create) / Step 3 (analyze): Practice ── */}
          {((currentStep === 2 && !hasAnalyze) || (currentStep === 3 && hasAnalyze)) && (
            <PracticeStep
              classTitle={cls.title}
              classId={cls.id}
              category={cls.category}
              fromAnalyze={hasAnalyze && currentStep === 3}
              onComplete={handlePracticeComplete}
            />
          )}

          {/* ── Submit step ── */}
          {currentStep === submitStep && (
            <SubmitStep prompt={builtPrompt} onSubmit={() => completeStep(submitStep)} />
          )}

          {/* ── Feedback step ── */}
          {currentStep === feedbackStep && (
            <FeedbackStep
              prompt={builtPrompt}
              classId={cls.id}
              classTitle={cls.title}
            />
          )}

          {/* Back Button */}
          {isBackVisible && (
            <div className="flex justify-center mt-2">
              <button
                onClick={() => {
                  setCurrentStep((s) => s - 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="flex items-center gap-2 text-sm text-[#8B95A1] hover:text-[#191F28] transition-colors cursor-pointer py-2 px-4 rounded-full hover:bg-white"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line" />
                </span>
                이전 단계로
              </button>
            </div>
          )}
        </div>
      </div>

      <FocusTimer classId={cls.id} classTitle={cls.title} />
    </div>
  );
}
